import { create } from 'zustand';
import type { Profile } from '../lib/store/types.ts';

interface AppState {
  profile: Profile | null;
  setProfile: (p: Profile | null) => void;
  /** The signed-in user's profiles, for stale-while-revalidate rendering on the profile picker
   * and Home: whatever was last fetched shows at once while the fresh list loads. Mirrored in
   * localStorage so the first screen after opening the app is never empty; cleared on sign-out
   * (every sign-out path calls `setProfiles(null)`). */
  profiles: Profile[] | null;
  setProfiles: (ps: Profile[] | null) => void;
  /** Reviewer flag mode (docs/reviewer_flags.md): whether it's *on* right now, toggled from the
   * parent dashboard. Distinct from whether the signed-in account is even allowed to turn it on
   * (that's checked fresh from `GET /v1/me` each time the dashboard loads, never cached here) —
   * this only remembers the child screens' current display state across navigation, and is
   * persisted so the toggle survives a reload while reviewing a lesson end to end. */
  reviewMode: boolean;
  setReviewMode: (on: boolean) => void;
}

const KEY = 'ezishizi:profile';
const PROFILES_KEY = 'ezishizi:profiles';
const REVIEW_MODE_KEY = 'ezishizi:reviewMode';

function storedProfiles(): Profile[] | null {
  try {
    const raw = localStorage.getItem(PROFILES_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Profile[]) : null;
  } catch {
    return null;
  }
}

export const useApp = create<AppState>((set) => ({
  profile: null,
  setProfile: (p) => {
    try {
      if (p) localStorage.setItem(KEY, p.id);
      else localStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
    set({ profile: p });
  },
  profiles: storedProfiles(),
  setProfiles: (ps) => {
    try {
      if (ps) localStorage.setItem(PROFILES_KEY, JSON.stringify(ps));
      else localStorage.removeItem(PROFILES_KEY);
    } catch {
      /* ignore */
    }
    set({ profiles: ps });
  },
  reviewMode: (() => {
    try {
      return localStorage.getItem(REVIEW_MODE_KEY) === '1';
    } catch {
      return false;
    }
  })(),
  setReviewMode: (on) => {
    try {
      if (on) localStorage.setItem(REVIEW_MODE_KEY, '1');
      else localStorage.removeItem(REVIEW_MODE_KEY);
    } catch {
      /* ignore */
    }
    set({ reviewMode: on });
  },
}));

export function rememberedProfileId(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}
